// A Bluetooth QR gun paired with the tablet types what it reads as a keyboard. This listens to the
// whole document, tells a gun's burst (keys a few ms apart) from a person typing, and reads the
// order id out of the feedback QR printed on the verification ticket:
//   https://loyalty.bronze.vision/survey?id=<order_id>
// (printer/lambdas/print_ticket in BronzeTracker; the id is bronze.order.id).
//
// The gun sends key positions, not characters: when it is set to a US layout and the tablet to a
// Spanish one, «/» arrives as «-», «?» as «_», «=» as «¡» and «:» as «Ñ». So every burst is kept
// twice, as the characters the tablet produced (`key`) and as what a US layout would have produced
// from the same keys (`code`), and both are tried.

// Most guns send a key every 5-40 ms; a person on a hardware keyboard rarely goes under 120 ms.
// Measured on the event's own time, so a busy main thread (a list re-render, a photo encode) that
// delivers ten queued keys late does not cut a scan in two.
export const MAX_KEY_GAP_MS = 100;
// No terminator (Enter/Tab) configured on the gun: the burst ends when the keys stop.
export const IDLE_END_MS = 120;
// Shorter than any QR worth reading; keeps stray fast keys (a person's double tap) out.
export const MIN_SCAN_LENGTH = 8;

const US = {
  Minus: ['-', '_'], Equal: ['=', '+'], BracketLeft: ['[', '{'], BracketRight: [']', '}'],
  Backslash: ['\\', '|'], Semicolon: [';', ':'], Quote: ["'", '"'], Comma: [',', '<'],
  Period: ['.', '>'], Slash: ['/', '?'], Backquote: ['`', '~'], Space: [' ', ' '],
  Digit1: ['1', '!'], Digit2: ['2', '@'], Digit3: ['3', '#'], Digit4: ['4', '$'], Digit5: ['5', '%'],
  Digit6: ['6', '^'], Digit7: ['7', '&'], Digit8: ['8', '*'], Digit9: ['9', '('], Digit0: ['0', ')'],
  NumpadSubtract: ['-', '-'], NumpadDecimal: ['.', '.'], NumpadDivide: ['/', '/'],
};

// What a US layout prints for this physical key, or null when it prints nothing.
export function usCharFromCode(code, shiftKey) {
  if (!code) return null;
  const letter = /^Key([A-Z])$/.exec(code);
  if (letter) return shiftKey ? letter[1] : letter[1].toLowerCase();
  const numpad = /^Numpad(\d)$/.exec(code);
  if (numpad) return numpad[1];
  const pair = US[code];
  return pair ? pair[shiftKey ? 1 : 0] : null;
}

const SURVEY = /loyalty\.bronze\.vision\/survey\?(?:[^#]*&)?id=([^&#\s]+)/i;

// The order id in a feedback URL, or null when the text is not one.
export function parseFeedbackOrderId(text) {
  if (!text) return null;
  const match = SURVEY.exec(String(text));
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch (e) {
    return match[1];
  }
}

const FEEDBACK_HINT = /loyalty|bronze|survey/i;

// One burst read by the gun: the order id when it is a feedback QR, and the text as it arrived.
// `partial` is a feedback QR that arrived cut (a piece of the URL but no id to read): worth a
// «scan again», not a «that is not an order».
// Order ids are case-sensitive (Square's, for one). With Caps Lock on, the tablet inverts the case of
// every letter the gun types (`key`), while the US reading (`code`) takes the case from the gun's own
// Shift: when the two read the same id but for its case, the US reading is the right one.
export function readScan({ key, code }) {
  const fromKey = parseFeedbackOrderId(key), fromCode = parseFeedbackOrderId(code);
  const caseOnly = fromKey && fromCode && fromKey !== fromCode && fromKey.toLowerCase() === fromCode.toLowerCase();
  const orderId = caseOnly ? fromCode : (fromKey || fromCode);
  return {
    orderId,
    text: key,
    usText: code,
    layoutMismatch: !parseFeedbackOrderId(key) && Boolean(orderId),
    partial: !orderId && (FEEDBACK_HINT.test(key) || FEEDBACK_HINT.test(code)),
  };
}

// Listens until stop() is called. `onScan` gets readScan()'s result for every burst long enough to be
// a scan, feedback QR or not, so a demo shows what the gun really typed.
export function startQrScanner({ onScan, target = document, now = null } = {}) {
  let key = '';
  let code = '';
  let last = 0;
  let lastScanAt = -Infinity;
  let idleTimer = null;

  const reset = () => {
    key = '';
    code = '';
    clearTimeout(idleTimer);
    idleTimer = null;
  };

  const finish = () => {
    const burst = { key, code };
    reset();
    if (burst.key.length < MIN_SCAN_LENGTH && burst.code.length < MIN_SCAN_LENGTH) return false;
    lastScanAt = last;
    onScan(readScan(burst));
    return true;
  };

  const onKeydown = (event) => {
    const t = now ? now() : (event.timeStamp || Date.now());
    const gap = t - last;
    last = t;
    if (event.isComposing) return;
    if (event.key === 'Enter' || event.key === 'Tab') {
      // The gun's terminator, sometimes two (CR+LF, Enter+Tab). Swallowed so it does not press the
      // button that still has the focus (the last order tapped) nor move the focus.
      if ((gap <= MAX_KEY_GAP_MS * 3 && finish()) || t - lastScanAt <= MAX_KEY_GAP_MS * 3) {
        event.preventDefault();
        event.stopPropagation();
      } else {
        reset();
      }
      return;
    }
    // A dead key of the tablet's layout (« ' » or « ` » on a Spanish one) prints nothing yet, but
    // the US reading of the same key still counts.
    const dead = event.key === 'Dead';
    if (!dead && event.key && event.key.length !== 1) return; // Shift and the like
    if (event.ctrlKey || event.metaKey || (event.altKey && !event.getModifierState?.('AltGraph'))) return;
    if (gap > MAX_KEY_GAP_MS) {
      // Too slow for a gun: a person typing, or the first key of a new burst.
      key = '';
      code = '';
    }
    if (dead) {
      code += usCharFromCode(event.code, event.shiftKey) ?? '';
      clearTimeout(idleTimer);
      idleTimer = setTimeout(finish, IDLE_END_MS);
      return;
    }
    key += event.key;
    code += usCharFromCode(event.code, event.shiftKey) ?? event.key;
    clearTimeout(idleTimer);
    idleTimer = setTimeout(finish, IDLE_END_MS);
  };

  target.addEventListener('keydown', onKeydown, true);
  return () => {
    reset();
    target.removeEventListener('keydown', onKeydown, true);
  };
}
